import { getId } from '@/store/utils'
import { normalizeHexColor } from '../color'
import {
  mergeTagByTitle,
  normalizeTagTitle,
  sanitizeTagTitle,
} from '../modelUtils'
import type { XmlImportTagItem } from '../types'
import { getChildElements, getFirstChildElement } from './dom'

const splitTagList = (value: string): string[] =>
  value
    .split(/[,\n;|]+/)
    .map(sanitizeTagTitle)
    .filter(Boolean)

const parseTagColor = (tagElement: Element): string | null =>
  normalizeHexColor(tagElement.getAttribute('color') || '')

interface ParsedTagDraft {
  title: string
  colorHex?: string
}

const mergeTagItems = (items: ParsedTagDraft[]): XmlImportTagItem[] => {
  const tagsByKey = new Map<string, XmlImportTagItem>()

  items.forEach((item) => {
    mergeTagByTitle({
      tagsByKey,
      title: item.title,
      ...(item.colorHex ? { colorHex: item.colorHex } : {}),
      createId: getId,
    })
  })

  return Array.from(tagsByKey.values())
}

const parseProjectTagsElement = (
  tagsElement: Element | null
): XmlImportTagItem[] => {
  if (!tagsElement) {
    return []
  }

  const tagItems = getChildElements(tagsElement, 'tag').flatMap(
    (tagElement) => {
      const colorHex = parseTagColor(tagElement)
      return splitTagList(tagElement.textContent?.trim() || '').map(
        (title) => ({
          title,
          ...(colorHex ? { colorHex } : {}),
        })
      )
    }
  )

  return mergeTagItems(tagItems)
}

export const parseProjectTags = (rootElement: Element): XmlImportTagItem[] =>
  parseProjectTagsElement(getFirstChildElement(rootElement, ['tags']))

export const parseNodeTags = (nodeElement: Element): string[] => {
  const tagsByKey = new Map<string, string>()
  const titles = splitTagList(nodeElement.getAttribute('tag') || '')

  titles.forEach((title) => {
    const key = normalizeTagTitle(title)
    if (key && !tagsByKey.has(key)) {
      tagsByKey.set(key, title)
    }
  })

  return Array.from(tagsByKey.values())
}
